// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selection Form Component

import { useState, useEffect } from 'react';
import { EnhancedSelection, SelectionTag } from '@/types/selection';
import { Plant } from '@/types/plant';
import { SensoryData } from '@/types/sensory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Info, X } from 'lucide-react';
import { SelectionTagList, SelectionTag as SelectionTagComponent } from './SelectionTag';
import { TraitRadarChart } from './TraitRadarChart';
import { createTag } from '@/utils/selectionUtils';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

// Common tag categories and suggestions
const TAG_SUGGESTIONS = {
  aroma: ['fruity', 'citrus', 'pine', 'diesel', 'earthy', 'floral', 'sweet', 'spicy', 'cheese', 'skunk'],
  flavor: ['sweet', 'woody', 'spicy', 'citrus', 'fruity', 'berry', 'earthy', 'nutty', 'coffee', 'cream'],
  appearance: ['purple', 'dense', 'frosty', 'orange', 'green', 'red', 'bright', 'dark', 'large', 'compact'],
  growth: ['vigorous', 'bushy', 'tall', 'short', 'branchy', 'stretchy', 'early', 'late', 'uniform', 'sturdy'],
  effects: ['relaxing', 'uplifting', 'creative', 'energetic', 'focused', 'sleepy', 'euphoric', 'calm', 'hungry', 'talkative'],
  terpenes: ['myrcene', 'limonene', 'caryophyllene', 'terpinolene', 'pinene', 'linalool', 'humulene', 'ocimene', 'terpineol', 'bisabolol'],
};

// Ash color options
const ASH_COLORS = [
  { value: 'white', label: 'White', color: '#FFFFFF' },
  { value: 'light-gray', label: 'Light Gray', color: '#D3D3D3' },
  { value: 'medium-gray', label: 'Medium Gray', color: '#808080' },
  { value: 'dark-gray', label: 'Dark Gray', color: '#404040' },
  { value: 'black', label: 'Black', color: '#000000' }
];

interface SelectionFormProps {
  selection?: EnhancedSelection;
  plant: Plant;
  sensoryData?: SensoryData;
  onSubmit: (selection: EnhancedSelection) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function SelectionForm({
  selection,
  plant,
  sensoryData,
  onSubmit,
  onCancel,
  isLoading = false
}: SelectionFormProps) {
  const isEditing = !!selection;
  
  // Form state
  const [formState, setFormState] = useState<Partial<EnhancedSelection>>({
    id: selection?.id || '',
    phenoId: selection?.phenoId || plant.phenotype_id,
    plantId: selection?.plantId || plant.plant_id,
    generation: selection?.generation || 'F1',
    selectionDate: selection?.selectionDate || new Date().toISOString(),
    selectionRationale: selection?.selectionRationale || '',
    selectionNotes: selection?.selectionNotes || '',
    selectionHighlights: selection?.selectionHighlights || [],
    growthMetrics: selection?.growthMetrics || {
      structure: 5,
      vigor: 5,
      yield: 5,
      frost: 5,
      overall: 5
    },
    tags: selection?.tags || [],
    badges: selection?.badges || [],
    images: selection?.images || [],
    sensoryData: selection?.sensoryData || sensoryData || {
      aroma_intensity: 5,
      aroma_quality: 5,
      aroma_profiles: [],
      flavor_intensity: 5,
      flavor_quality: 5,
      flavor_profiles: [],
      smoke_harshness: 5,
      ash_color: 'white'
    }
  });
  
  // Highlight input
  const [highlightInput, setHighlightInput] = useState('');
  
  // Tag input
  const [tagInput, setTagInput] = useState('');
  const [tagCategory, setTagCategory] = useState<string>('aroma');
  
  // Local state for date picker
  const [date, setDate] = useState<Date>(
    selection?.selectionDate 
      ? new Date(selection.selectionDate) 
      : new Date()
  );
  
  // Update the form state when the date changes
  useEffect(() => {
    setFormState(prev => ({
      ...prev,
      selectionDate: date.toISOString()
    }));
  }, [date]);
  
  // Update form state for growth metrics
  const handleGrowthMetricChange = (metric: keyof EnhancedSelection['growthMetrics'], value: number[]) => {
    setFormState(prev => ({
      ...prev,
      growthMetrics: {
        ...prev.growthMetrics!,
        [metric]: value[0]
      }
    }));
  };
  
  // Update form state for sensory metrics
  const handleSensoryMetricChange = (
    metric: keyof NonNullable<EnhancedSelection['sensoryData']>, 
    value: number[] | string | string[]
  ) => {
    setFormState(prev => ({
      ...prev,
      sensoryData: {
        ...prev.sensoryData!,
        [metric]: Array.isArray(value) && typeof value[0] === 'number' 
          ? value[0] 
          : value
      }
    }));
  };
  
  // Add a highlight
  const addHighlight = () => {
    if (highlightInput.trim()) {
      setFormState(prev => ({
        ...prev,
        selectionHighlights: [...(prev.selectionHighlights || []), highlightInput.trim()]
      }));
      setHighlightInput('');
    }
  };
  
  // Remove a highlight
  const removeHighlight = (index: number) => {
    setFormState(prev => ({
      ...prev,
      selectionHighlights: (prev.selectionHighlights || []).filter((_, i) => i !== index)
    }));
  };
  
  // Add a tag
  const addTag = () => {
    if (tagInput.trim()) {
      const newTag = createTag(tagInput.trim(), tagCategory);
      if (!formState.tags?.some(t => t.name.toLowerCase() === newTag.name.toLowerCase())) {
        setFormState(prev => ({
          ...prev,
          tags: [...(prev.tags || []), newTag]
        }));
      }
      setTagInput('');
    }
  };
  
  // Add a suggested tag
  const addSuggestedTag = (tag: string) => {
    const newTag = createTag(tag, tagCategory);
    if (!formState.tags?.some(t => t.name.toLowerCase() === newTag.name.toLowerCase())) {
      setFormState(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag]
      }));
    }
  };
  
  // Remove a tag
  const removeTag = (tagToRemove: SelectionTag) => {
    setFormState(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag.id !== tagToRemove.id)
    }));
  };
  
  // Add aroma/flavor profile
  const addProfile = (profile: string, type: 'aroma' | 'flavor') => {
    const key = `${type}_profiles` as keyof NonNullable<EnhancedSelection['sensoryData']>;
    const profiles = [...(formState.sensoryData?.[key] as string[] || [])];
    
    if (!profiles.includes(profile)) {
      profiles.push(profile);
      handleSensoryMetricChange(key, profiles);
    }
  };
  
  // Remove aroma/flavor profile
  const removeProfile = (profile: string, type: 'aroma' | 'flavor') => {
    const key = `${type}_profiles` as keyof NonNullable<EnhancedSelection['sensoryData']>;
    const profiles = (formState.sensoryData?.[key] as string[] || []).filter(p => p !== profile);
    handleSensoryMetricChange(key, profiles);
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await onSubmit(formState as EnhancedSelection);
    } catch (error) {
      console.error('Error submitting selection:', error);
    }
  };
  
  // Check if form is valid
  const isFormValid = () => {
    return (
      formState.plantId &&
      formState.generation &&
      formState.selectionDate &&
      formState.growthMetrics?.overall !== undefined
    );
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs defaultValue="basics" className="space-y-4">
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="basics">Basic Info</TabsTrigger>
          <TabsTrigger value="growth">Growth Metrics</TabsTrigger>
          <TabsTrigger value="sensory">Sensory Data</TabsTrigger>
          <TabsTrigger value="tags">Tags & Images</TabsTrigger>
        </TabsList>
        
        {/* Basic Info Tab */}
        <TabsContent value="basics" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plantId">Plant ID</Label>
              <Input
                id="plantId"
                value={formState.plantId}
                onChange={(e) => setFormState(prev => ({ ...prev, plantId: e.target.value }))}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="generation">Generation</Label>
              <Input
                id="generation"
                value={formState.generation}
                onChange={(e) => setFormState(prev => ({ ...prev, generation: e.target.value }))}
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="selectionDate">Selection Date</Label>
            <div className="grid grid-cols-1 gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className="w-full justify-start text-left font-normal"
                  >
                    <span>{format(date, "PPP")}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(newDate) => newDate && setDate(newDate)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <div>
            <Label htmlFor="selectionRationale">Selection Rationale</Label>
            <Textarea
              id="selectionRationale"
              placeholder="Why was this plant selected? What characteristics stand out?"
              value={formState.selectionRationale || ''}
              onChange={(e) => setFormState(prev => ({ ...prev, selectionRationale: e.target.value }))}
              className="min-h-[120px]"
            />
          </div>
          
          <div>
            <Label htmlFor="selectionNotes">Notes</Label>
            <Textarea
              id="selectionNotes"
              placeholder="Additional notes about this selection..."
              value={formState.selectionNotes || ''}
              onChange={(e) => setFormState(prev => ({ ...prev, selectionNotes: e.target.value }))}
              className="min-h-[120px]"
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center">
              <Label htmlFor="highlights">Highlights</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">
                      Key points that will be displayed on the selection card.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <div className="flex gap-2 mt-2 mb-3">
              <Input
                id="highlights"
                placeholder="Add a highlight (e.g., 'Strong terpene profile')"
                value={highlightInput}
                onChange={(e) => setHighlightInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
              />
              <Button type="button" onClick={addHighlight}>Add</Button>
            </div>
            
            <div className="space-y-2">
              {(formState.selectionHighlights || []).map((highlight, index) => (
                <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-md">
                  <span className="text-sm">{highlight}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeHighlight(index)}
                    className="h-6 w-6"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {(formState.selectionHighlights?.length || 0) === 0 && (
                <div className="text-sm text-gray-500 italic p-2">
                  No highlights added. Highlights help summarize key features of this selection.
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* Growth Metrics Tab */}
        <TabsContent value="growth" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="structure">Structure ({formState.growthMetrics?.structure})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="structure"
                  value={[formState.growthMetrics?.structure || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleGrowthMetricChange('structure', value)}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="vigor">Vigor ({formState.growthMetrics?.vigor})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="vigor"
                  value={[formState.growthMetrics?.vigor || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleGrowthMetricChange('vigor', value)}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="yield">Yield ({formState.growthMetrics?.yield})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="yield"
                  value={[formState.growthMetrics?.yield || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleGrowthMetricChange('yield', value)}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="frost">Frost ({formState.growthMetrics?.frost})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="frost"
                  value={[formState.growthMetrics?.frost || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleGrowthMetricChange('frost', value)}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="overall" className="font-semibold">Overall ({formState.growthMetrics?.overall})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="overall"
                  value={[formState.growthMetrics?.overall || 5]}
                  min={1}
                  max={10}
                  step={0.5}
                  onValueChange={(value) => handleGrowthMetricChange('overall', value)}
                />
              </div>
            </div>
            
            {/* Visualization */}
            <div className="flex flex-col items-center justify-center">
              <Card className="w-full">
                <CardContent className="pt-6">
                  <div className="text-sm font-medium mb-2 text-center">Growth Metrics Visualization</div>
                  <div className="flex justify-center">
                    <TraitRadarChart
                      selection={formState as EnhancedSelection}
                      width={250}
                      height={250}
                      showLabels={true}
                    />
                  </div>
                </CardContent>
              </Card>
              <p className="text-sm text-gray-500 mt-4 text-center max-w-md">
                This visualization updates as you adjust the metrics. It helps visualize the overall
                growth profile of your selection.
              </p>
            </div>
          </div>
        </TabsContent>
        
        {/* Sensory Data Tab */}
        <TabsContent value="sensory" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="aroma_intensity">Aroma Intensity ({formState.sensoryData?.aroma_intensity})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="aroma_intensity"
                  value={[formState.sensoryData?.aroma_intensity || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleSensoryMetricChange('aroma_intensity', value)}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="aroma_quality">Aroma Quality ({formState.sensoryData?.aroma_quality})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="aroma_quality"
                  value={[formState.sensoryData?.aroma_quality || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleSensoryMetricChange('aroma_quality', value)}
                />
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="aroma_profiles">Aroma Profiles</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(formState.sensoryData?.aroma_profiles || []).map((profile) => (
                    <Badge 
                      key={profile} 
                      className="bg-purple-100 hover:bg-purple-200 text-purple-800 cursor-pointer"
                      onClick={() => removeProfile(profile, 'aroma')}
                    >
                      {profile}
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {TAG_SUGGESTIONS.aroma.filter(t => 
                    !(formState.sensoryData?.aroma_profiles || []).includes(t)
                  ).map((suggestion) => (
                    <Badge 
                      key={suggestion} 
                      variant="outline" 
                      className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                      onClick={() => addProfile(suggestion, 'aroma')}
                    >
                      + {suggestion}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="flavor_intensity">Flavor Intensity ({formState.sensoryData?.flavor_intensity})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="flavor_intensity"
                  value={[formState.sensoryData?.flavor_intensity || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleSensoryMetricChange('flavor_intensity', value)}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="flavor_quality">Flavor Quality ({formState.sensoryData?.flavor_quality})</Label>
                  <span className="text-sm text-gray-500">
                    1-10
                  </span>
                </div>
                <Slider
                  id="flavor_quality"
                  value={[formState.sensoryData?.flavor_quality || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleSensoryMetricChange('flavor_quality', value)}
                />
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="flavor_profiles">Flavor Profiles</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(formState.sensoryData?.flavor_profiles || []).map((profile) => (
                    <Badge 
                      key={profile} 
                      className="bg-purple-100 hover:bg-purple-200 text-purple-800 cursor-pointer"
                      onClick={() => removeProfile(profile, 'flavor')}
                    >
                      {profile}
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {TAG_SUGGESTIONS.flavor.filter(t => 
                    !(formState.sensoryData?.flavor_profiles || []).includes(t)
                  ).map((suggestion) => (
                    <Badge 
                      key={suggestion} 
                      variant="outline" 
                      className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                      onClick={() => addProfile(suggestion, 'flavor')}
                    >
                      + {suggestion}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label htmlFor="smoke_harshness">
                    Smoke Harshness ({formState.sensoryData?.smoke_harshness})
                  </Label>
                  <span className="text-sm text-gray-500">
                    1-10 (lower is smoother)
                  </span>
                </div>
                <Slider
                  id="smoke_harshness"
                  value={[formState.sensoryData?.smoke_harshness || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleSensoryMetricChange('smoke_harshness', value)}
                />
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="ash_color">Ash Color</Label>
                <Select 
                  value={formState.sensoryData?.ash_color || 'white'}
                  onValueChange={(value) => handleSensoryMetricChange('ash_color', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select ash color" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {ASH_COLORS.map(color => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center">
                            <div 
                              className="h-3 w-3 rounded-full mr-2 border border-gray-300" 
                              style={{ 
                                backgroundColor: color.color,
                                borderColor: color.value === 'white' ? '#D1D5DB' : 'transparent'
                              }} 
                            />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <div className="text-sm font-medium mb-2 text-center">Sensory Profile Visualization</div>
                  <div className="flex justify-center">
                    <TraitRadarChart
                      selection={formState as EnhancedSelection}
                      width={250}
                      height={250}
                      showLabels={true}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <div className="text-sm text-gray-600 italic mt-4 p-4 bg-gray-50 rounded-md">
                <p className="font-medium mb-1">Evaluation Tips:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Aroma evaluation is best done at room temperature</li>
                  <li>Quality ratings should consider complexity and appeal</li>
                  <li>Ash color is a good indicator of proper curing and mineral content</li>
                  <li>Lower smoke harshness scores indicate smoother product</li>
                </ul>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Tags & Images Tab */}
        <TabsContent value="tags" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="tags">Tags</Label>
                <div className="flex gap-2 mt-2 mb-3">
                  <Select value={tagCategory} onValueChange={setTagCategory}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(TAG_SUGGESTIONS).map(category => (
                        <SelectItem key={category} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </SelectItem>
                      ))}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Input
                    id="tags"
                    placeholder="Add a tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag}>Add</Button>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm text-gray-500 mb-2">Selected Tags:</div>
                  <div className="min-h-[100px] p-3 border rounded-md bg-gray-50">
                    {formState.tags && formState.tags.length > 0 ? (
                      <SelectionTagList 
                        tags={formState.tags}
                        onTagClick={(tag) => removeTag(tag)}
                      />
                    ) : (
                      <div className="text-sm text-gray-500 italic">
                        No tags added. Tags help categorize and filter your selections.
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="text-sm text-gray-500 mb-2">Suggested tags for {tagCategory}:</div>
                  <div className="flex flex-wrap gap-2">
                    {TAG_SUGGESTIONS[tagCategory as keyof typeof TAG_SUGGESTIONS]?.filter(
                      t => !formState.tags?.some(tag => tag.name.toLowerCase() === t.toLowerCase())
                    ).map((suggestion) => (
                      <Badge 
                        key={suggestion} 
                        variant="outline" 
                        className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                        onClick={() => addSuggestedTag(suggestion)}
                      >
                        + {suggestion}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="images">Images</Label>
                <div className="text-sm text-gray-500 mb-2">
                  Upload images of this selection.
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Image upload placeholder - would integrate with your image upload system */}
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center">
                    <span className="text-gray-500 text-sm">
                      Image upload functionality would be integrated here
                    </span>
                  </div>
                  
                  {formState.images && formState.images.length > 0 && (
                    formState.images.map((image, index) => (
                      <div key={index} className="relative rounded-md overflow-hidden">
                        <img 
                          src={image} 
                          alt={`Selection ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6 rounded-full"
                          onClick={() => {
                            setFormState(prev => ({
                              ...prev,
                              images: prev.images?.filter((_, i) => i !== index)
                            }));
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-md">
                <h3 className="text-sm font-medium mb-2 flex items-center">
                  <AlertCircle className="h-4 w-4 text-amber-500 mr-2" />
                  Badge Generation Info
                </h3>
                <p className="text-sm text-gray-600">
                  Badges will be automatically generated based on your selection's metrics and tags. 
                  High values in particular categories will earn special badges to highlight your 
                  selection's standout characteristics.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isFormValid() || isLoading}>
          {isLoading ? 'Saving...' : isEditing ? 'Update Selection' : 'Create Selection'}
        </Button>
      </div>
    </form>
  );
} 